/**
 * TanStack Query hooks + query-param builders for the /merchant Staff area.
 *
 * These endpoints (`GET /admin/users` list, `GET /admin/merchants` list,
 * `GET /admin/merchants/{id}/document`, …) are either absent from or stale in
 * the generated OpenAPI `paths` map, so — exactly like `features/kds/api.ts`,
 * `features/orders/hooks.ts`, `features/merchants/hooks.ts` — we hit them with
 * a hand-rolled authorized fetch that reuses the SHARED auth primitives:
 * `getAccessToken` (token injection), `tryRefresh` (the single refresh mutex),
 * `clearTokens` + login bounce, and the structured `ApiError` envelope. This
 * is the same behaviour the `openapi-fetch` middleware in `api/client.ts`
 * gives generated paths — re-implemented for the ungenerated ones.
 *
 * Fixed by TASK-PLAN P5: builders + hook signatures are a contract for the 7
 * screen-agents. Query keys are `['staff', <resource>, params]`.
 */
import {
  useMutation,
  useQueryClient,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { cnpj as fmtCnpj, cpf as fmtCpf } from '@favelafood/shared/lib/merchant/format';
import type { DocumentStatus, MerchantStatus, Role } from '@favelafood/shared/lib/merchant/status';
import type {
  AdminMerchantListItem,
  DashboardStats,
  MerchantDocumentResponse,
  MerchantResponse,
  PageResponse,
  PendingItem,
  PendingKind,
  UserResponse,
} from '@favelafood/shared/api/types';
import { merchantApiFetch, toQuery, compact, type FetchInit } from '@favelafood/shared/api/fetcher';

// --------------------------------------------------------------------------
// Shared fetch is now in client.ts — re-exported alias for backward compat
// --------------------------------------------------------------------------

const staffFetch = merchantApiFetch;

const ADMIN_BASE = '/api/v1/admin';

// FetchInit re-exported via client.ts import — used by mutations below.
export type { FetchInit };

// --------------------------------------------------------------------------
// Query-param builders (pure — unit-tested)
// --------------------------------------------------------------------------

export interface UserParams {
  role?: Role;
  documentStatus?: DocumentStatus;
  isActive?: boolean;
  page?: number;
  size?: number;
}

export interface MerchantParams {
  status?: MerchantStatus;
  categoryId?: string;
  /** FAV-139 — scope the list to one owner ("comércios desse comerciante"). */
  ownerProfileId?: string;
  /** FAV-138 — server-side text search (nome fantasia / razão social). */
  q?: string;
  page?: number;
  size?: number;
}

export function buildUserParams(p: UserParams): Record<string, string> {
  return compact([
    ['role', p.role],
    ['documentStatus', p.documentStatus],
    ['isActive', p.isActive],
    ['page', p.page],
    ['size', p.size],
  ]);
}

export function buildMerchantParams(p: MerchantParams): Record<string, string> {
  return compact([
    ['status', p.status],
    ['categoryId', p.categoryId],
    ['ownerProfileId', p.ownerProfileId],
    ['q', p.q],
    ['page', p.page],
    ['size', p.size],
  ]);
}

// --------------------------------------------------------------------------
// Wire coercion (BigDecimal fields can arrive as JSON strings — bug-pattern #7)
// --------------------------------------------------------------------------

/** Coerce a possibly-string numeric field to `number | undefined`. */
const num = (raw: unknown): number | undefined => {
  if (raw === null || raw === undefined) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

/** Coerce a possibly-string numeric field to `number` (0 fallback). */
const num0 = (raw: unknown): number => num(raw) ?? 0;

/**
 * `{ [key]: value }` when `value` is defined, else `{}`. Lets us spread a
 * coerced numeric over the raw object WITHOUT assigning an explicit
 * `undefined` (which `exactOptionalPropertyTypes` rejects for `field?: T`).
 */
const opt = <K extends string>(key: K, value: number | undefined): Record<K, number> | Record<string, never> =>
  value === undefined ? {} : ({ [key]: value } as Record<K, number>);

const mapDashboardStats = (raw: DashboardStats): DashboardStats => ({
  totalOrdersToday: num0(raw.totalOrdersToday),
  totalRevenueToday: num0(raw.totalRevenueToday),
  activeCouriers: num0(raw.activeCouriers),
  pendingMerchants: num0(raw.pendingMerchants),
  totalUsers: num0(raw.totalUsers),
  totalMerchants: num0(raw.totalMerchants),
});

const mapMerchant = (raw: MerchantResponse): MerchantResponse => ({
  ...raw,
  ...opt('minimumOrderAmount', num(raw.minimumOrderAmount)),
  ...opt('averageRating', num(raw.averageRating)),
  ...opt('latitude', num(raw.latitude)),
  ...opt('longitude', num(raw.longitude)),
});

// --------------------------------------------------------------------------
// Hooks
// --------------------------------------------------------------------------

export function useDashboardStats(): UseQueryResult<DashboardStats> {
  return useQuery<DashboardStats>({
    queryKey: ['staff', 'dashboard-stats'],
    queryFn: async () => mapDashboardStats(await staffFetch<DashboardStats>(`${ADMIN_BASE}/dashboard/stats`)),
  });
}

export function useUsers(p: UserParams): UseQueryResult<PageResponse<UserResponse>> {
  const params = buildUserParams(p);
  return useQuery<PageResponse<UserResponse>>({
    queryKey: ['staff', 'users', params],
    queryFn: () => staffFetch<PageResponse<UserResponse>>(`${ADMIN_BASE}/users${toQuery(params)}`),
  });
}

export function useUser(id: string): UseQueryResult<UserResponse> {
  return useQuery<UserResponse>({
    queryKey: ['staff', 'user', id],
    queryFn: () => staffFetch<UserResponse>(`${ADMIN_BASE}/users/${encodeURIComponent(id)}`),
    enabled: Boolean(id),
  });
}

/**
 * `GET /admin/merchants` — desde FAV-137 o payload é o `AdminMerchantListItem`
 * ENRIQUECIDO (cnpjMasked, ownerName, ordersCount, addressLine,
 * ownerMerchantsCount). O tipo antigo (`MerchantResponse`) escondia esses
 * campos que já chegavam na rede — era a causa das colunas stubadas no painel.
 */
export function useMerchants(p: MerchantParams): UseQueryResult<PageResponse<AdminMerchantListItem>> {
  const params = buildMerchantParams(p);
  return useQuery<PageResponse<AdminMerchantListItem>>({
    queryKey: ['staff', 'merchants', params],
    queryFn: () =>
      staffFetch<PageResponse<AdminMerchantListItem>>(`${ADMIN_BASE}/merchants${toQuery(params)}`),
  });
}

// FAV-144: reads GET /admin/merchants/{id} (any status). Replaces the old public
// vitrine read (APPROVED-only) so non-approved shops load in the Staff shop-detail.
export function useMerchant(id: string): UseQueryResult<MerchantResponse> {
  return useQuery<MerchantResponse>({
    queryKey: ['staff', 'merchant', id],
    queryFn: async () => mapMerchant(await staffFetch<MerchantResponse>(`${ADMIN_BASE}/merchants/${encodeURIComponent(id)}`)),
    enabled: Boolean(id),
  });
}

export function useMerchantDocument(id: string): UseQueryResult<MerchantDocumentResponse> {
  return useQuery<MerchantDocumentResponse>({
    queryKey: ['staff', 'merchant-document', id],
    queryFn: () => staffFetch<MerchantDocumentResponse>(`${ADMIN_BASE}/merchants/${encodeURIComponent(id)}/document`),
    enabled: Boolean(id),
  });
}

// --------------------------------------------------------------------------
// Unified pending queue (merge of pending merchant-people + pending shops)
// --------------------------------------------------------------------------

/** Params for the three pending sources. Generous `size` to grab the full queue. */
const PENDING_USERS = buildUserParams({ role: 'MERCHANT', documentStatus: 'PENDING', page: 0, size: 100 });
const PENDING_MERCHANTS = buildMerchantParams({ status: 'PENDING', page: 0, size: 100 });
// STA-9: couriers with pending document review — same `GET /admin/users` source, role COURIER.
const PENDING_COURIERS = buildUserParams({ role: 'COURIER', documentStatus: 'PENDING', page: 0, size: 100 });

/** Count-only params: `size:1` so the badge query never pulls 100 rows. */
const PENDING_USERS_COUNT = buildUserParams({ role: 'MERCHANT', documentStatus: 'PENDING', size: 1 });
const PENDING_MERCHANTS_COUNT = buildMerchantParams({ status: 'PENDING', size: 1 });
const PENDING_COURIERS_COUNT = buildUserParams({ role: 'COURIER', documentStatus: 'PENDING', size: 1 });

const userToPending = (u: UserResponse): PendingItem => ({
  kind: 'user',
  id: u.id,
  name: u.fullName,
  sub: u.email,
  doc: fmtCpf(u.cpf),
  createdAt: u.createdAt,
});

const courierToPending = (u: UserResponse): PendingItem => ({
  kind: 'courier',
  id: u.id,
  name: u.fullName,
  sub: u.email,
  doc: fmtCpf(u.cpf),
  createdAt: u.createdAt,
});

const merchantToPending = (m: AdminMerchantListItem): PendingItem => ({
  kind: 'merchant',
  id: m.id,
  name: m.name,
  sub: m.category ?? '—',
  // cnpjMasked já vem mascarado do servidor (FAV-137/LGPD) — exibe como está.
  doc: m.cnpjMasked ?? fmtCnpj(undefined),
  createdAt: m.createdAt,
});

/**
 * Unified, oldest-first pending queue: pending merchant-people (kind `user`,
 * doc=CPF) + pending shops (kind `merchant`, doc=CNPJ) + pending couriers
 * (kind `courier`, doc=CPF — STA-9). Merchants in `PENDING_OWNER_ACTIVATION`
 * are intentionally excluded — they wait on the owner accepting the invite,
 * not on staff approval.
 *
 * Driven by a single `useQuery` (all three endpoints fetched in the queryFn)
 * so it returns a genuine, fully-formed `UseQueryResult` — no hand-rolled state.
 */
export function usePendingQueue(): UseQueryResult<PendingItem[]> {
  return useQuery<PendingItem[]>({
    queryKey: ['staff', 'pending-queue'],
    queryFn: async () => {
      const [users, merchants, couriers] = await Promise.all([
        staffFetch<PageResponse<UserResponse>>(`${ADMIN_BASE}/users${toQuery(PENDING_USERS)}`),
        staffFetch<PageResponse<AdminMerchantListItem>>(`${ADMIN_BASE}/merchants${toQuery(PENDING_MERCHANTS)}`),
        staffFetch<PageResponse<UserResponse>>(`${ADMIN_BASE}/users${toQuery(PENDING_COURIERS)}`),
      ]);
      const items = [
        ...users.content.map(userToPending),
        ...merchants.content.map(merchantToPending),
        ...couriers.content.map(courierToPending),
      ];
      items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return items;
    },
  });
}

/**
 * Pending badge count = `totalElements` of pending merchant-people + pending
 * shops + pending couriers (STA-9). Returns 0 while any source is still
 * loading. Uses `size:1` count queries so the badge never transfers the full queue.
 */
export function usePendingCount(): number {
  const users = useQuery<PageResponse<UserResponse>>({
    queryKey: ['staff', 'users', PENDING_USERS_COUNT],
    queryFn: () => staffFetch<PageResponse<UserResponse>>(`${ADMIN_BASE}/users${toQuery(PENDING_USERS_COUNT)}`),
  });
  const merchants = useQuery<PageResponse<AdminMerchantListItem>>({
    queryKey: ['staff', 'merchants', PENDING_MERCHANTS_COUNT],
    queryFn: () =>
      staffFetch<PageResponse<AdminMerchantListItem>>(`${ADMIN_BASE}/merchants${toQuery(PENDING_MERCHANTS_COUNT)}`),
  });
  const couriers = useQuery<PageResponse<UserResponse>>({
    queryKey: ['staff', 'users', PENDING_COURIERS_COUNT],
    queryFn: () => staffFetch<PageResponse<UserResponse>>(`${ADMIN_BASE}/users${toQuery(PENDING_COURIERS_COUNT)}`),
  });
  return (
    (users.data?.totalElements ?? 0) +
    (merchants.data?.totalElements ?? 0) +
    (couriers.data?.totalElements ?? 0)
  );
}

// --------------------------------------------------------------------------
// Approve / reject — pure request builders (unit-tested) + mutation hooks
// --------------------------------------------------------------------------

/** The two write actions on a pending-queue row. */
export type PendingAction = 'approve' | 'reject';

/**
 * Pure mapping from a pending item + action to the `PATCH` request to send.
 * Centralizes the per-kind contract (spec §2.1) so the hooks and tests share
 * one source of truth:
 *
 *   - `user`/`courier` → `PATCH /admin/users/{id}/document-status` (STA-9: same
 *                         endpoint for either role — it's keyed by user id, not role)
 *                         approve `{status:'APPROVED'}`; reject `{status:'REJECTED', rejectionReason}`.
 *   - `merchant`       → `PATCH /admin/merchants/{id}/status`
 *                         approve `{status:'APPROVED'}`; reject `{status:'REJECTED', reasonCode, reasonNote}`.
 *
 * V64: recusar uma LOJA passou a exigir motivo no backend (@AssertTrue no UpdateStatusRequest) —
 * antes o texto digitado era descartado no caminho. Sem `reasonCode` a chamada volta 400.
 */
export function pendingActionRequest(
  item: Pick<PendingItem, 'kind' | 'id'>,
  action: PendingAction,
  reason?: string,
): { path: string; body: Record<string, unknown> } {
  const status = action === 'approve' ? 'APPROVED' : 'REJECTED';
  if (item.kind === 'merchant') {
    const body: Record<string, unknown> =
      action === 'reject'
        ? { status, reasonCode: 'DOCUMENTO_INVALIDO', reasonNote: reason }
        : { status };
    return { path: `${ADMIN_BASE}/merchants/${item.id}/status`, body };
  }
  const body: Record<string, unknown> =
    action === 'reject' ? { status, rejectionReason: reason } : { status };
  return { path: `${ADMIN_BASE}/users/${item.id}/document-status`, body };
}

/** V64: TODOS os tipos exigem motivo agora — o backend recusa sem ele nos 3 caminhos. */
export function rejectReasonRequired(_kind: PendingKind): boolean {
  return true;
}

/**
 * Invalidate every query the approve/reject mutations can stale: the unified
 * pending queue (+ badge count, keyed under `['staff','users'|'merchants']`)
 * and the people/shop lists. Both write paths share this so a card leaving the
 * queue, the sidebar badge, and the lists all refresh together (spec §2.1).
 */
function useInvalidatePending(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['staff', 'pending-queue'] });
    void qc.invalidateQueries({ queryKey: ['staff', 'users'] });
    void qc.invalidateQueries({ queryKey: ['staff', 'merchants'] });
  };
}

/** Approve a pending merchant-person: `PATCH document-status {APPROVED}`. */
export function useApproveUser(): UseMutationResult<void, unknown, string> {
  const invalidate = useInvalidatePending();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => {
      const { path, body } = pendingActionRequest({ kind: 'user', id }, 'approve');
      return staffFetch<void>(path, { method: 'PATCH', body });
    },
    onSuccess: invalidate,
  });
}

/** Reject a pending merchant-person: `PATCH document-status {REJECTED, rejectionReason}`. */
export function useRejectUser(): UseMutationResult<void, unknown, { id: string; reason: string }> {
  const invalidate = useInvalidatePending();
  return useMutation<void, unknown, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => {
      const { path, body } = pendingActionRequest({ kind: 'user', id }, 'reject', reason);
      return staffFetch<void>(path, { method: 'PATCH', body });
    },
    onSuccess: invalidate,
  });
}

/** Approve a pending shop: `PATCH merchants/{id}/status {APPROVED}`. */
export function useApproveMerchant(): UseMutationResult<void, unknown, string> {
  const invalidate = useInvalidatePending();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => {
      const { path, body } = pendingActionRequest({ kind: 'merchant', id }, 'approve');
      return staffFetch<void>(path, { method: 'PATCH', body });
    },
    onSuccess: invalidate,
  });
}

/**
 * Reject a pending shop: `PATCH merchants/{id}/status {REJECTED, reasonCode, reasonNote}`.
 *
 * V64: o motivo e OBRIGATORIO no backend e o operador e obrigado a digitar no modal. Esta mutation
 * aceitava so o `id` e descartava o texto no caminho — o log gravava nota vazia sempre.
 */
export function useRejectMerchant(): UseMutationResult<
  void,
  unknown,
  { id: string; reason: string }
> {
  const invalidate = useInvalidatePending();
  return useMutation<void, unknown, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => {
      const { path, body } = pendingActionRequest({ kind: 'merchant', id }, 'reject', reason);
      return staffFetch<void>(path, { method: 'PATCH', body });
    },
    onSuccess: invalidate,
  });
}

/**
 * Transição genérica de status da loja — `PATCH /admin/merchants/{id}/status`
 * (STAFF-allowed, FAV-143; transições validadas no backend, ex.
 * `APPROVED↔SUSPENDED`). Usada pelo Suspender/Reativar do shop-detail.
 */
export function useSetMerchantStatus(): UseMutationResult<
  void,
  unknown,
  { id: string; status: MerchantStatus; reasonCode?: string; reasonNote?: string }
> {
  const qc = useQueryClient();
  const invalidate = useInvalidatePending();
  return useMutation<
    void,
    unknown,
    { id: string; status: MerchantStatus; reasonCode?: string; reasonNote?: string }
  >({
    // V64: REJECTED e SUSPENDED exigem `reasonCode` (@AssertTrue no UpdateStatusRequest) — sem ele
    // o backend devolve 400. APPROVED/PENDING seguem sem motivo.
    mutationFn: ({ id, status, reasonCode, reasonNote }) =>
      staffFetch<void>(`${ADMIN_BASE}/merchants/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body: {
          status,
          ...(reasonCode ? { reasonCode } : {}),
          ...(reasonNote ? { reasonNote } : {}),
        },
      }),
    onSuccess: (_data, { id }) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['staff', 'merchant', id] });
    },
  });
}
